import { useMemo } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { cn } from '@/lib/utils';
import { resolveEmbeddedImages } from '@/lib/embeds';

interface RichContentProps {
  html: string;
  className?: string;
  /** When true, detects simple wrapper HTML containing markdown syntax and parses it as markdown.
   *  Use only for task comments — does not affect content with real rich HTML (bold, links, mentions, etc.) */
  markdownFallback?: boolean;
  /** The message's attachments; inline embeds take their current `url` from here. */
  attachments?: Array<{ id: string; url?: string | null }> | null;
}

// Configure marked for safer output
marked.setOptions({
  gfm: true, // GitHub Flavored Markdown
  breaks: true, // Convert \n to <br>
});

// Configure DOMPurify to open links in new tabs
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
  // Lazy load images and constrain size to prevent layout issues
  if (node.tagName === 'IMG') {
    node.setAttribute('loading', 'lazy');
    if (!node.getAttribute('style')) {
      node.setAttribute('style', 'max-width: 100%; height: auto;');
    }
  }
});

/**
 * Detects if content is HTML or Markdown
 */
function isHtml(content: string): boolean {
  // Check if it starts with HTML tags or contains common HTML patterns
  const htmlPatterns = /^<[a-z][\s\S]*>/i;
  return htmlPatterns.test(content.trim()) || content.includes('</');
}

/**
 * Detects if HTML contains meaningful rich markup beyond simple wrappers.
 * Returns true for content with formatting (strong, em, links, lists, mentions, etc.)
 * Returns false for content that is just <p>, <div>, <br>, <span> wrapping plain text.
 */
function hasRichHtml(content: string): boolean {
  const richPatterns = /<(strong|em|b|i|a\s|ul|ol|li|h[1-6]|img|blockquote|pre|code|table|thead|tbody|tr|td|th|hr)\b/i;
  const mentionPattern = /data-type\s*=\s*["']mention["']/i;
  return richPatterns.test(content) || mentionPattern.test(content);
}

/**
 * Extracts plain text from simple wrapper HTML (p, div, br, span without special attrs).
 * Converts block-level closing tags to newlines to preserve paragraph structure.
 */
function extractTextFromSimpleHtml(content: string): string {
  let text = content;
  // Convert </p>, </div>, <br>, <br/> to newlines
  text = text.replace(/<\/p>/gi, '\n');
  text = text.replace(/<\/div>/gi, '\n');
  text = text.replace(/<br\s*\/?>/gi, '\n');
  // Strip all remaining tags
  text = text.replace(/<[^>]+>/g, '');
  // Decode HTML entities
  text = text.replace(/&amp;/g, '&');
  text = text.replace(/&lt;/g, '<');
  text = text.replace(/&gt;/g, '>');
  text = text.replace(/&quot;/g, '"');
  text = text.replace(/&#39;/g, "'");
  text = text.replace(/&nbsp;/g, ' ');
  // Trim trailing whitespace but preserve internal newlines
  return text.trim();
}

/**
 * Detects if plain text contains markdown syntax worth parsing.
 */
function hasMarkdownSyntax(text: string): boolean {
  const markdownPatterns = [
    /\*\*[^*]+\*\*/,        // **bold**
    /\*[^*]+\*/,            // *italic*
    /__[^_]+__/,            // __bold__
    /_[^_]+_/,              // _italic_
    /~~[^~]+~~/,            // ~~strikethrough~~
    /\[[^\]]+\]\([^)]+\)/,  // [link](url)
    /^#{1,6}\s/m,           // # heading
    /^[-*+]\s/m,            // - list item
    /^\d+\.\s/m,            // 1. ordered list
    /^>\s/m,                // > blockquote
    /`[^`]+`/,              // `inline code`
    /^```/m,                // ```code block```
  ];
  return markdownPatterns.some(pattern => pattern.test(text));
}

/**
 * Safely renders HTML or Markdown content
 */
export function RichContent({ html, className, markdownFallback = false, attachments }: RichContentProps) {
  const sanitizedHtml = useMemo(() => {
    if (!html) return '';

    // Embeds store a presigned src that has long expired; re-point them first.
    let content = resolveEmbeddedImages(html, attachments);

    // If content is not HTML, parse it as Markdown
    if (!isHtml(content)) {
      content = marked.parse(content) as string;
    } else if (markdownFallback && !hasRichHtml(content)) {
      // Content is simple wrapper HTML (e.g. <p>**bold**</p> from TipTap).
      // Extract plain text and check for markdown syntax.
      const plainText = extractTextFromSimpleHtml(content);
      if (plainText && hasMarkdownSyntax(plainText)) {
        content = marked.parse(plainText) as string;
      }
    }

    // Sanitize the output
    return DOMPurify.sanitize(content, {
      USE_PROFILES: { html: true },
      ADD_ATTR: ['target', 'rel', 'style', 'loading'],
    });
  }, [html, markdownFallback, attachments]);

  return (
    <div 
      className={cn(
        'prose prose-sm max-w-none break-words [&_a]:break-all',
        'prose-headings:font-serif prose-headings:text-secondary',
        'prose-p:text-secondary prose-p:my-2 prose-p:leading-relaxed',
        'prose-ul:my-2 prose-ol:my-2',
        'prose-li:my-0.5',
        'prose-blockquote:border-l-primary prose-blockquote:text-grey-600 prose-blockquote:not-italic',
        'prose-code:bg-grey-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-mono',
        'prose-pre:bg-grey-900 prose-pre:text-grey-100 prose-pre:rounded-lg',
        '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
        'prose-a:text-primary prose-a:underline prose-a:underline-offset-2 hover:prose-a:text-primary-600',
        'prose-strong:font-semibold prose-strong:text-secondary',
        'prose-em:italic',
        className
      )}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
}

